import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, map, takeUntil } from 'rxjs/operators';

import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import {
  EntitySelectComponent,
  EntitySelectPage
} from '../../../shared/components/entity-select/entity-select.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { UserApiService } from '../../users/services/user-api.service';
import { CrmUserResponse } from '../../users/models/user.model';
import { TicketsApiService } from '../services/tickets-api.service';
import { TicketsConfigApiService } from '../services/tickets-config-api.service';
import { TicketsDrawerService } from '../services/tickets-drawer.service';
import { humanText } from '../../../shared/utils/api-error.util';
import {
  Ticket,
  TicketBulkAction,
  TicketBulkFailure,
  TicketFilterCondition,
  TicketFilterOperator,
  TicketFilterRequest,
  TicketSource
} from '../models/tickets.model';
import {
  TicketCategory,
  TicketPipeline,
  TicketPipelineStage,
  TicketPriority,
  TicketSavedView,
  TicketStatus
} from '../models/tickets-config.model';
import { TicketsDetailModalComponent } from '../tickets-detail-modal/tickets-detail-modal.component';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { getOperatorLabel, getOrigenLabel, getScopeLabel } from '../utils/ticket-format.util';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

interface AdvancedConditionForm {
  field: string;
  operator: TicketFilterOperator;
  valueText: string;
  /** Texto legible para entidades (contacto/empresa) al mostrar en el EntitySelect. */
  valueLabel?: string;
}

/** Fila de la tabla: puede ser un ticket padre (con o sin hijos) o un subticket indentado. */
interface TicketRow {
  ticket: Ticket;
  isChild: boolean;
  hasChildren: boolean;
  expanded: boolean;
}

const FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: 'subject', label: 'Asunto' },
  { value: 'description', label: 'Descripción' },
  { value: 'priority', label: 'Prioridad' },
  { value: 'status', label: 'Estado' },
  { value: 'category', label: 'Categoría' },
  { value: 'pipeline', label: 'Pipeline' },
  { value: 'stage', label: 'Etapa' },
  { value: 'owner', label: 'Asignado a' },
  { value: 'contact', label: 'Contacto' },
  { value: 'company', label: 'Empresa' },
  { value: 'source', label: 'Origen' },
  { value: 'ticketNumber', label: 'Número' },
  { value: 'createdAt', label: 'Fecha de creación' },
  { value: 'resolvedAt', label: 'Fecha de resolución' },
  { value: 'csatScore', label: 'CSAT' }
];

const OPERATOR_OPTIONS: TicketFilterOperator[] = [
  'EQ',
  'NEQ',
  'IN',
  'NOT_IN',
  'GT',
  'GTE',
  'LT',
  'LTE',
  'LIKE',
  'IS_NULL',
  'IS_NOT_NULL'
];

const SOURCE_OPTIONS: TicketSource[] = [
  'MANUAL',
  'EMAIL',
  'CHAT',
  'PHONE',
  'WEB_FORM',
  'PORTAL',
  'SOCIAL',
  'API'
];

/** Fields cuyo valor es un id numérico (se convierten a number en el filtro). */
const NUMERIC_FIELDS = new Set([
  'priority',
  'status',
  'category',
  'pipeline',
  'stage',
  'owner',
  'contact',
  'company',
  'csatScore'
]);

/** Tipos de vínculo que representan jerarquía padre→hijo. */
const HIERARCHY_LINK_TYPES = new Set(['SUB_TICKET', 'SPLIT_FROM']);

type FieldKind =
  | 'priority'
  | 'status'
  | 'category'
  | 'pipeline'
  | 'stage'
  | 'owner'
  | 'contact'
  | 'company'
  | 'source'
  | 'csat'
  | 'date'
  | 'text';

@Component({
  selector: 'app-tickets-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    ModalComponent,
    EntitySelectComponent,
    TicketsDetailModalComponent,
    HasPermissionDirective
  ],
  templateUrl: './tickets-list.component.html'
})
export class TicketsListComponent implements OnInit, OnDestroy {
  tickets: Ticket[] = [];
  /** Filas planas ya agrupadas por jerarquía (padres + hijos expandidos). */
  displayRows: TicketRow[] = [];
  loading = false;
  error = '';

  // Catálogos
  priorities: TicketPriority[] = [];
  statuses: TicketStatus[] = [];
  categories: TicketCategory[] = [];
  pipelines: TicketPipeline[] = [];
  allStages: TicketPipelineStage[] = [];
  users: CrmUserResponse[] = [];

  // Saved views
  savedViews: TicketSavedView[] = [];
  selectedSavedViewId: number | null = null;
  /** true cuando el usuario cambió filtros respecto de la vista guardada seleccionada. */
  viewDirty = false;

  // Filtros simples
  /**
   * Texto libre de la bandeja. Ya no es solo el asunto: el backend lo compara también
   * contra la descripción, el número del ticket y el nombre del contacto y de la empresa.
   */
  showFilters = false;
  filtroTexto = '';
  filtroIdStatus: number | undefined;
  filtroIdPriority: number | undefined;
  filtroIdCategory: number | undefined;

  // Búsqueda avanzada (overrides los simples cuando está activa)
  advancedActive = false;
  advancedCombinator: 'AND' | 'OR' = 'AND';
  advancedConditions: AdvancedConditionForm[] = [];
  showAdvancedModal = false;
  showSaveViewModal = false;
  newViewName = '';
  newViewScope: 'PRIVATE' | 'PUBLIC' | 'TEAM' = 'PRIVATE';

  readonly fieldOptions = FIELD_OPTIONS;
  readonly operatorOptions = OPERATOR_OPTIONS;
  readonly sourceOptions = SOURCE_OPTIONS;
  readonly csatOptions = [1, 2, 3, 4, 5];

  // Etiquetas legibles (helpers de plantilla)
  origenLabel = (s?: string | null) => getOrigenLabel(s);
  operatorLabel = (op?: string | null) => getOperatorLabel(op);
  scopeLabel = (s?: string | null) => getScopeLabel(s);

  /**
   * V180 — vista de archivados. Archivar no cambia el estado del ticket, así que sin
   * esta vista los archivados no se verían en ninguna parte. No se persiste en
   * localStorage a propósito: la lista debe abrir siempre en la operación del día.
   */
  verArchivados = false;

  // Owner filter
  private readonly OWNER_FILTER_KEY = 'crm_owner_filter_tickets';
  filtroOwner: 'all' | 'mine' =
    localStorage.getItem(this.OWNER_FILTER_KEY) === 'mine' ? 'mine' : 'all';

  // Paginación
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'tickets-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  // Bulk selection
  selectedIds = new Set<number>();
  bulkInFlight = false;

  // Modal de detalle
  showDetailModal = false;
  selectedTicket: Ticket | null = null;

  // Jerarquía padre/hijo
  private childrenMap = new Map<number, Ticket[]>();
  private childIds = new Set<number>();
  private expandedParents = new Set<number>();

  private readonly destroy$ = new Subject<void>();
  /** Freno del buscador de texto. Ver {@link buscarTexto}. */
  private readonly busquedaTexto$ = new Subject<void>();

  constructor(
    protected router: Router,
    private route: ActivatedRoute,
    private ticketsApi: TicketsApiService,
    private configApi: TicketsConfigApiService,
    private authService: AuthService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private userApi: UserApiService,
    private drawerService: TicketsDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarCatalogos();
    this.cargarSavedViews();
    this.cargarTickets();

    // Freno del buscador de texto: la busqueda va al servidor en cada tecla.
    this.busquedaTexto$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => this.buscar());

    // Refrescar la lista cuando se guarda/fusiona un ticket desde el drawer (INC 30).
    this.drawerService.ticketSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargarTickets());

    // INC 50: al llegar con ?openTicket=<id> (p.ej. desde una notificación),
    // abrir el modal de detalle del ticket indicado sobre la lista.
    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const raw = params.get('openTicket');
        const id = raw != null ? Number(raw) : NaN;
        if (!Number.isNaN(id)) {
          this.abrirDetalleModalPorId(id);
        }
      });
  }

  /** INC 50: carga el ticket completo por id y abre el modal de detalle. */
  private abrirDetalleModalPorId(id: number): void {
    this.ticketsApi
      .getTicketById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ticket) => {
          this.selectedTicket = ticket;
          this.showDetailModal = true;
        },
        error: () => {
          this.toast.error({
            title: 'No se pudo abrir el ticket',
            message: 'El ticket no existe o no tienes acceso.'
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.busquedaTexto$.complete();
  }

  // ===== Catálogos =====
  private cargarCatalogos(): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;

    forkJoin({
      priorities: this.configApi.getPriorities().pipe(catchError(() => of([] as TicketPriority[]))),
      statuses: this.configApi.getStatuses().pipe(catchError(() => of([] as TicketStatus[]))),
      categories: this.configApi.getCategories().pipe(catchError(() => of([] as TicketCategory[]))),
      pipelines: this.configApi.getPipelines().pipe(catchError(() => of([] as TicketPipeline[]))),
      users: this.userApi
        .listActiveByBusiness(businessId)
        .pipe(catchError(() => of([] as CrmUserResponse[])))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ priorities, statuses, categories, pipelines, users }) => {
        this.priorities = priorities;
        this.statuses = statuses;
        this.categories = categories;
        this.pipelines = pipelines;
        this.users = users.filter((u) => u.isActive !== false);
        this.loadAllStages();
      });
  }

  /** Carga las etapas de todos los pipelines para el selector de "Etapa" en búsqueda avanzada. */
  private loadAllStages(): void {
    if (this.pipelines.length === 0) {
      this.allStages = [];
      return;
    }
    forkJoin(
      this.pipelines.map((p) =>
        this.configApi
          .getPipelineStages(p.id)
          .pipe(catchError(() => of([] as TicketPipelineStage[])))
      )
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((perPipeline) => {
        this.allStages = perPipeline.flat();
      });
  }

  private cargarSavedViews(): void {
    this.configApi
      .getSavedViews()
      .pipe(catchError(() => of([] as TicketSavedView[])), takeUntil(this.destroy$))
      .subscribe((views) => (this.savedViews = views));
  }

  // ===== Carga de datos =====
  cargarTickets(): void {
    this.loading = true;
    this.error = '';
    this.clearSelection();

    const filter = this.buildFilter();
    this.ticketsApi
      .searchTicketsAdvanced(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // INC 30: por seguridad, ocultar fusionados/eliminados en cliente.
          this.tickets = (response.content ?? []).filter(
            (t) => t.isMerged !== true && t.isDeleted !== true
          );
          this.globalPage = response.number ?? response.page ?? this.globalPage;
          this.globalTotalPages = response.totalPages;
          this.globalTotalElements = response.totalElements;
          this.loadHierarchy(this.tickets);
        },
        error: (err) => {
          // INC 40: el backend devuelve un mensaje accionable en español (400/422).
          // Mostrarlo tal cual, además del banner inline.
          const msg = err.error?.message || 'Error al cargar tickets';
          this.error = msg;
          this.toast.error({ title: 'Error en la búsqueda', message: msg });
          this.loading = false;
        }
      });
  }

  /**
   * INC 27 + 32: agrupa subtickets/divisiones bajo su padre cuando ambos están en la
   * página cargada.
   *
   * Inc 73: el árbol se arma con `idTicketParent` / `parentLinkType`, que ya vienen en
   * cada ticket de la respuesta de la lista. Antes se hacía un `getLinks(id)` por cada
   * ticket de la página (N+1 peticiones); ahora no hay ninguna llamada extra.
   */
  private loadHierarchy(tickets: Ticket[]): void {
    this.childrenMap.clear();
    this.childIds.clear();

    if (tickets.length === 0) {
      this.expandedParents.clear();
      this.displayRows = [];
      this.loading = false;
      return;
    }

    const idsInPage = new Set(tickets.map((t) => t.id));

    for (const child of tickets) {
      const parentId = child.idTicketParent;
      if (parentId == null || parentId === child.id) continue;
      // Sólo vínculos de jerarquía (SUB_TICKET / SPLIT_FROM). Si el backend no envía
      // el tipo, se asume jerarquía porque idTicketParent ya la representa.
      const type = (child.parentLinkType || 'SUB_TICKET').toUpperCase();
      if (!HIERARCHY_LINK_TYPES.has(type)) continue;
      // El padre debe estar en la página cargada; si no, el hijo se pinta suelto.
      if (!idsInPage.has(parentId)) continue;
      this.childIds.add(child.id);
      if (!this.childrenMap.has(parentId)) this.childrenMap.set(parentId, []);
      this.childrenMap.get(parentId)!.push(child);
    }

    // Por defecto, todos los padres con hijos quedan expandidos.
    this.expandedParents = new Set(this.childrenMap.keys());
    this.rebuildRows();
    this.loading = false;
  }

  /** Aplana padres + hijos (según expansión) en una lista ordenada de filas. */
  private rebuildRows(): void {
    const rows: TicketRow[] = [];
    for (const t of this.tickets) {
      if (this.childIds.has(t.id)) continue; // los hijos se pintan bajo su padre
      const kids = this.childrenMap.get(t.id) ?? [];
      const hasChildren = kids.length > 0;
      const expanded = this.expandedParents.has(t.id);
      rows.push({ ticket: t, isChild: false, hasChildren, expanded });
      if (hasChildren && expanded) {
        for (const k of kids) {
          rows.push({ ticket: k, isChild: true, hasChildren: false, expanded: false });
        }
      }
    }
    this.displayRows = rows;
  }

  toggleExpand(id: number): void {
    if (this.expandedParents.has(id)) {
      this.expandedParents.delete(id);
    } else {
      this.expandedParents.add(id);
    }
    this.rebuildRows();
  }

  private buildFilter(): TicketFilterRequest {
    const conditions: TicketFilterCondition[] = [];

    if (this.advancedActive) {
      for (const c of this.advancedConditions) {
        if (!c.field || !c.operator) continue;
        const cond: TicketFilterCondition = { field: c.field, operator: c.operator };
        if (c.operator !== 'IS_NULL' && c.operator !== 'IS_NOT_NULL') {
          const numeric = NUMERIC_FIELDS.has(c.field);
          if (c.operator === 'IN' || c.operator === 'NOT_IN') {
            const parts = (c.valueText || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            cond.value = numeric ? parts.map((p) => Number(p)) : parts;
          } else {
            cond.value = numeric ? Number(c.valueText) : c.valueText;
          }
        }
        conditions.push(cond);
      }
    } else {
      if (this.filtroTexto?.trim()) {
        // `anyText` NO es una columna: el backend lo expande a asunto + descripción +
        // número + nombre del contacto + nombre de la empresa, sin acentos. Con
        // `subject` no se podía encontrar un ticket por su número ni por su cliente.
        conditions.push({ field: 'anyText', operator: 'LIKE', value: this.filtroTexto.trim() });
      }
      if (this.filtroIdStatus !== undefined) {
        conditions.push({ field: 'status', operator: 'EQ', value: this.filtroIdStatus });
      }
      if (this.filtroIdPriority !== undefined) {
        conditions.push({ field: 'priority', operator: 'EQ', value: this.filtroIdPriority });
      }
      if (this.filtroIdCategory !== undefined) {
        conditions.push({ field: 'category', operator: 'EQ', value: this.filtroIdCategory });
      }
      if (this.filtroOwner === 'mine') {
        const userId = this.authService.getCurrentUser()?.id;
        if (userId !== undefined && userId !== null) {
          conditions.push({ field: 'owner', operator: 'EQ', value: userId });
        }
      }
    }

    return {
      combinator: this.advancedActive ? this.advancedCombinator : 'AND',
      conditions,
      page: this.globalPage,
      size: this.pageSize,
      // En la vista de archivados ordenar por creación no dice nada: lo relevante es
      // cuándo se archivó. El backend acepta ambos campos en el whitelist de orden.
      sortField: this.verArchivados ? 'archivedAt' : 'datetimeCreated',
      sortDirection: 'DESC',
      archived: this.verArchivados
    };
  }

  /** V180 — alterna entre la operación diaria y los tickets archivados. */
  setArchivedView(archived: boolean): void {
    if (this.verArchivados === archived) return;
    this.verArchivados = archived;
    this.globalPage = 0;
    this.cargarTickets();
  }

  // ===== Filtros simples =====

  /**
   * Cada tecla dispara una búsqueda en el servidor, así que el texto va con freno: escribir
   * «TKT-2026-00034» lanzaba 15 consultas y solo importaba la última. Los selectores siguen
   * llamando a {@link buscar} directo — ahí cada cambio es una decisión, no una tecla.
   */
  buscarTexto(): void {
    this.busquedaTexto$.next();
  }

  buscar(): void {
    this.globalPage = 0;
    if (this.selectedSavedViewId != null) this.viewDirty = true;
    this.cargarTickets();
  }

  setOwnerFilter(value: 'all' | 'mine'): void {
    this.filtroOwner = value;
    localStorage.setItem(this.OWNER_FILTER_KEY, value);
    this.globalPage = 0;
    if (this.selectedSavedViewId != null) this.viewDirty = true;
    this.cargarTickets();
  }

  limpiarFiltro(): void {
    this.filtroOwner = 'all';
    localStorage.setItem(this.OWNER_FILTER_KEY, 'all');
    this.filtroTexto = '';
    this.filtroIdStatus = undefined;
    this.filtroIdPriority = undefined;
    this.filtroIdCategory = undefined;
    this.advancedActive = false;
    this.advancedConditions = [];
    this.selectedSavedViewId = null;
    this.viewDirty = false;
    this.globalPage = 0;
    this.cargarTickets();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  // ===== Saved Views =====
  onSelectSavedView(viewId: number | null): void {
    this.selectedSavedViewId = viewId;
    this.viewDirty = false;
    if (viewId == null) {
      this.advancedActive = false;
      this.advancedConditions = [];
      this.buscar();
      return;
    }
    const view = this.savedViews.find((v) => v.id === viewId);
    if (!view) return;

    // Deserializar el filter
    const filter = view.filter as TicketFilterRequest | null;
    if (filter && Array.isArray(filter.conditions)) {
      this.advancedActive = true;
      this.advancedCombinator = filter.combinator || 'AND';
      this.advancedConditions = filter.conditions.map((c) => ({
        field: c.field,
        operator: c.operator,
        valueText: this.serializeValue(c.value)
      }));
    } else {
      this.advancedActive = false;
      this.advancedConditions = [];
    }
    this.globalPage = 0;
    this.cargarTickets();
  }

  abrirSaveViewModal(): void {
    this.newViewName = '';
    this.newViewScope = 'PRIVATE';
    this.showSaveViewModal = true;
  }

  cerrarSaveViewModal(): void {
    this.showSaveViewModal = false;
  }

  guardarVista(): void {
    if (!this.newViewName.trim()) return;
    const filter = this.buildFilter();
    this.configApi
      .createSavedView({
        name: this.newViewName.trim(),
        scope: this.newViewScope,
        filter
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (created) => {
          this.toast.created('Vista guardada');
          this.savedViews = [...this.savedViews, created];
          this.selectedSavedViewId = created.id;
          this.viewDirty = false;
          this.showSaveViewModal = false;
        },
        error: (err) => {
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'No se pudo guardar la vista'
          });
        }
      });
  }

  async eliminarVista(): Promise<void> {
    if (!this.selectedSavedViewId) return;
    const view = this.savedViews.find((v) => v.id === this.selectedSavedViewId);
    if (!view) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar vista',
      message: `¿Está seguro de eliminar la vista "${view.name}"?`
    });
    if (!ok) return;

    this.configApi
      .deleteSavedView(view.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.deleted('Vista guardada');
          this.savedViews = this.savedViews.filter((v) => v.id !== view.id);
          this.selectedSavedViewId = null;
          this.viewDirty = false;
          this.advancedActive = false;
          this.advancedConditions = [];
          this.cargarTickets();
        },
        error: (err) => {
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'No se pudo eliminar la vista'
          });
        }
      });
  }

  private serializeValue(value: unknown): string {
    if (value == null) return '';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
  }

  // ===== Búsqueda avanzada =====
  abrirAdvancedModal(): void {
    if (this.advancedConditions.length === 0) {
      this.advancedConditions = [{ field: 'subject', operator: 'LIKE', valueText: '' }];
    }
    this.showAdvancedModal = true;
  }

  cerrarAdvancedModal(): void {
    this.showAdvancedModal = false;
  }

  agregarCondicion(): void {
    this.advancedConditions.push({ field: 'subject', operator: 'LIKE', valueText: '' });
  }

  removerCondicion(index: number): void {
    this.advancedConditions.splice(index, 1);
  }

  /** Al cambiar el campo, limpiar el valor previo (evita ids/valores obsoletos). */
  onFieldChange(cond: AdvancedConditionForm): void {
    cond.valueText = '';
    cond.valueLabel = '';
  }

  aplicarAdvanced(): void {
    this.advancedActive = this.advancedConditions.length > 0;
    this.selectedSavedViewId = null;
    this.viewDirty = true;
    this.globalPage = 0;
    this.showAdvancedModal = false;
    this.cargarTickets();
  }

  limpiarAdvanced(): void {
    this.advancedConditions = [];
    this.advancedActive = false;
    this.selectedSavedViewId = null;
    this.viewDirty = false;
    this.showAdvancedModal = false;
    this.cargarTickets();
  }

  needsValue(op: TicketFilterOperator): boolean {
    return op !== 'IS_NULL' && op !== 'IS_NOT_NULL';
  }

  /** Determina el tipo de editor de valor según el campo seleccionado. */
  fieldKind(field: string): FieldKind {
    switch (field) {
      case 'priority':
      case 'status':
      case 'category':
      case 'pipeline':
      case 'stage':
      case 'owner':
      case 'contact':
      case 'company':
      case 'source':
        return field;
      case 'csatScore':
        return 'csat';
      case 'createdAt':
      case 'resolvedAt':
        return 'date';
      default:
        return 'text';
    }
  }

  /** Valor id numérico de una condición (para EntitySelect). */
  condIdValue(cond: AdvancedConditionForm): number | null {
    const n = Number(cond.valueText);
    return cond.valueText && !isNaN(n) ? n : null;
  }

  onCondEntityChange(cond: AdvancedConditionForm, id: number | null): void {
    cond.valueText = id != null ? String(id) : '';
    if (id == null) cond.valueLabel = '';
  }

  // EntitySelect: contacto (server-side) y empresa (server-side)
  contactSearch = (term: string, page: number): Observable<EntitySelectPage<any>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.contactoApi
      .searchContacts({ businessId, searchTerm: term, page, size: 10 })
      .pipe(
        map((r) => ({ items: r.content, totalPages: r.totalPages })),
        catchError(() => of({ items: [] as any[], totalPages: 0 }))
      );
  };
  contactLabel = (c: any) => `${c?.fullname ?? ''}${c?.lastname ? ' ' + c.lastname : ''}`.trim();
  contactSublabel = (c: any) => c?.mail ?? '';

  companySearch = (term: string, page: number): Observable<EntitySelectPage<any>> => {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    return this.empresaApi
      .searchCompanies({ businessId, searchTerm: term, page, size: 10 })
      .pipe(
        map((r) => ({ items: r.content, totalPages: r.totalPages })),
        catchError(() => of({ items: [] as any[], totalPages: 0 }))
      );
  };
  companyLabel = (c: any) => c?.commercialName ?? '';
  companySublabel = (c: any) => c?.phone ?? '';

  userLabel = (u: CrmUserResponse) => `${u.fullname}${u.lastname ? ' ' + u.lastname : ''}`.trim();

  /** true cuando debe mostrarse el botón "Guardar como vista" (INC 35). */
  get canSaveAsView(): boolean {
    return this.selectedSavedViewId == null || this.viewDirty;
  }

  // ===== Bulk selection =====
  isSelected(id: number): boolean {
    return this.selectedIds.has(id);
  }

  toggleSelect(id: number): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  get allSelected(): boolean {
    return this.tickets.length > 0 && this.tickets.every((t) => this.selectedIds.has(t.id));
  }

  toggleAll(): void {
    if (this.allSelected) {
      this.tickets.forEach((t) => this.selectedIds.delete(t.id));
    } else {
      this.tickets.forEach((t) => this.selectedIds.add(t.id));
    }
  }

  clearSelection(): void {
    this.selectedIds.clear();
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  // ===== Bulk actions =====
  bulkChangeStatus(idStatus: number | null): void {
    if (idStatus == null) return;
    this.runBulk('CHANGE_STATUS', idStatus);
  }

  bulkChangePriority(idPriority: number | null): void {
    if (idPriority == null) return;
    this.runBulk('CHANGE_PRIORITY', idPriority);
  }

  bulkAssignTo(userId: number | null): void {
    if (userId == null) return;
    this.runBulk('ASSIGN', userId);
  }

  /**
   * V180 — archivado masivo. Confirma porque los tickets desaparecen de la vista
   * actual (siguen consultables en "Archivados").
   */
  async bulkArchive(): Promise<void> {
    if (this.selectedCount === 0) return;
    const ok = await this.confirmDialog.confirm({
      title: 'Archivar tickets',
      message: `¿Archivar ${this.selectedCount} ticket(s)? Saldrán de la lista y del kanban, ` +
        'pero podrás consultarlos en la pestaña "Archivados". No se cambia su estado.',
      confirmText: 'Archivar'
    });
    if (!ok) return;
    this.runBulk('ARCHIVE');
  }

  /** V180 — devuelve los seleccionados a la operación diaria. */
  bulkUnarchive(): void {
    if (this.selectedCount === 0) return;
    this.runBulk('UNARCHIVE');
  }

  /** V180 — archiva un ticket concreto desde su fila. */
  async archivarTicket(ticket: Ticket): Promise<void> {
    const ok = await this.confirmDialog.confirm({
      title: 'Archivar ticket',
      message: `¿Archivar el ticket ${ticket.ticketNumber}? Podrás consultarlo en "Archivados".`,
      confirmText: 'Archivar'
    });
    if (!ok) return;
    this.ticketsApi
      .archiveTicket(ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.success('Ticket archivado');
          this.cargarTickets();
        },
        error: (err) =>
          this.toast.error({
            title: 'No se archivó',
            message: err.error?.message || 'Error al archivar el ticket'
          })
      });
  }

  /** V180 — desarchiva un ticket concreto desde la vista de archivados. */
  desarchivarTicket(ticket: Ticket): void {
    this.ticketsApi
      .unarchiveTicket(ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.success('Ticket desarchivado');
          this.cargarTickets();
        },
        error: (err) =>
          this.toast.error({
            title: 'No se desarchivó',
            message: err.error?.message || 'Error al desarchivar el ticket'
          })
      });
  }

  private runBulk(action: TicketBulkAction, targetId?: number): void {
    if (this.selectedCount === 0) return;
    this.bulkInFlight = true;
    this.ticketsApi
      .bulkAction({
        ticketIds: Array.from(this.selectedIds),
        action,
        targetId
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.bulkInFlight = false;
          if (result.failed === 0) {
            const verbo =
              action === 'ARCHIVE' ? 'archivado(s)'
              : action === 'UNARCHIVE' ? 'desarchivado(s)'
              : 'actualizado(s)';
            this.toast.success(`${result.succeeded} ticket(s) ${verbo}`);
          } else {
            this.toast.warning({
              title: `${result.succeeded} correcto(s) / ${result.failed} con error`,
              message: result.failures.map((f) => this.describirFalloMasivo(f)).join('\n'),
              duration: 7000
            });
          }
          this.cargarTickets();
        },
        error: (err) => {
          this.bulkInFlight = false;
          this.toast.error({
            title: 'Error',
            message: err.error?.message || 'Error en la acción masiva'
          });
        }
      });
  }

  // ===== Paginación =====
  /**
   * Una línea del detalle de la acción masiva: el NÚMERO del ticket (TKT-…) en vez
   * del id interno, y el motivo del backend solo si es una frase; si vino un código
   * (`TICKET_ALREADY_ARCHIVED`) se muestra un texto genérico en español.
   */
  private describirFalloMasivo(f: TicketBulkFailure): string {
    const ticket = this.tickets.find((t) => t.id === f.ticketId);
    const etiqueta = ticket?.ticketNumber || ticket?.subject || 'Un ticket';
    const motivo = humanText(f.reason) ?? 'no se pudo procesar.';
    return `${etiqueta}: ${motivo}`;
  }

  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.cargarTickets();
  }

  changePageSize(size: number): void {
    this.pageSize = Number(size);
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.globalPage = 0;
    this.cargarTickets();
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }

  get hasPrevPage(): boolean {
    return this.globalPage > 0;
  }

  get hasNextPage(): boolean {
    return this.globalPage < this.globalTotalPages - 1;
  }

  get pageDisplay(): number {
    return this.globalPage + 1;
  }

  // ===== Helpers de catálogos =====
  getStatusLabel(idStatus: number | null): string {
    if (idStatus == null) return '-';
    return this.statuses.find((s) => s.id === idStatus)?.label || '-';
  }

  getPriorityLabel(idPriority: number | null): string {
    if (idPriority == null) return '-';
    return this.priorities.find((p) => p.id === idPriority)?.label || '-';
  }

  getCategoryName(idCategory: number | null): string {
    if (idCategory == null) return '-';
    return this.categories.find((c) => c.id === idCategory)?.name || '-';
  }

  getPriorityColor(idPriority: number | null): 'success' | 'warning' | 'error' | 'info' {
    if (idPriority == null) return 'info';
    const level = this.priorities.find((p) => p.id === idPriority)?.level;
    if (level === 1) return 'error';
    if (level === 2) return 'error';
    if (level === 3) return 'warning';
    if (level === 4) return 'success';
    return 'info';
  }

  getStatusColor(idStatus: number | null): 'success' | 'warning' | 'error' | 'info' {
    if (idStatus == null) return 'info';
    const status = this.statuses.find((s) => s.id === idStatus);
    if (!status) return 'info';
    if (status.isResolved) return 'success';
    if (status.isTerminal) return 'error';
    return 'warning';
  }

  // ===== Acciones =====
  vistaTablero(): void {
    this.router.navigate(['/tickets/kanban']);
  }

  nuevoTicket(): void {
    this.router.navigate(['/tickets/crear']);
  }

  editarTicket(ticket: Ticket): void {
    this.router.navigate(['/tickets/editar', ticket.id]);
  }

  async eliminarTicket(ticket: Ticket): Promise<void> {
    if (!ticket.id) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar ticket',
      message: `¿Está seguro de eliminar el ticket "${ticket.subject}"?`
    });
    if (!ok) return;

    this.ticketsApi
      .deleteTicket(ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.deleted('Ticket');
          this.cargarTickets();
        },
        error: (err) => {
          // INC 45: el backend devuelve mensajes específicos por código
          // (404 no encontrado, 409 sub-tickets/dependencias, 403 permisos).
          // Se muestra tal cual, con fallback genérico.
          this.toast.error({
            title: 'No se pudo eliminar',
            message: err.error?.message || 'Error al eliminar el ticket'
          });
        }
      });
  }

  // ===== Modal de detalle =====
  verDetalleModal(ticket: Ticket): void {
    this.selectedTicket = ticket;
    this.showDetailModal = true;
  }

  cerrarModalDetalle(): void {
    this.showDetailModal = false;
    this.selectedTicket = null;
    // INC 50: limpiar ?openTicket para permitir reabrir el mismo ticket luego.
    if (this.route.snapshot.queryParamMap.has('openTicket')) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { openTicket: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    }
  }
}
